import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CurrencyCircleDollar, TrendUp, TrendDown, ChartBar, Users, Truck } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const fmt = (v) => `£${(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

export default function ProfitLossPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/reports/profit-loss").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load P&L data")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!data) return <div className="text-center py-16 text-muted-foreground">No data available</div>;

  return (
    <div data-testid="profit-loss-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
          <CurrencyCircleDollar size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Profit & Loss</h1>
          <p className="text-xs text-muted-foreground">{data.total_invoices} invoices, {data.total_quotes} approved quotes</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="pl-kpis">
        <div className="border border-border rounded-lg p-4 bg-card">
          <TrendUp size={16} className="text-emerald-500 mb-1" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo] text-emerald-600">{fmt(data.total_revenue)}</p>
          <p className="text-[10px] text-muted-foreground">Total Revenue</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <TrendDown size={16} className="text-red-500 mb-1" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo]">{fmt(data.total_cost)}</p>
          <p className="text-[10px] text-muted-foreground">Total Costs</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <CurrencyCircleDollar size={16} className="text-emerald-500 mb-1" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo] text-emerald-600">{fmt(data.total_profit)}</p>
          <p className="text-[10px] text-muted-foreground">Net Profit</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <ChartBar size={16} className="text-blue-500 mb-1" weight="duotone" />
          <p className="text-xl font-bold font-[Chivo] text-blue-600">{data.profit_margin}%</p>
          <p className="text-[10px] text-muted-foreground">Profit Margin</p>
        </div>
      </div>

      {/* Monthly Chart */}
      {data.monthly?.length > 0 && (
        <div className="border border-border rounded-lg p-5 bg-card mb-6" data-testid="pl-monthly-chart">
          <h3 className="text-sm font-semibold font-[Chivo] mb-4">Monthly Breakdown</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
              <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" name="Cost" fill="#EF4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* By Client & By Supplier */}
      <div className="grid md:grid-cols-2 gap-4">
        {data.by_client?.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden" data-testid="pl-by-client">
            <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center gap-2">
              <Users size={16} className="text-blue-500" />
              <h3 className="text-sm font-semibold font-[Chivo]">Revenue by Client</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-border bg-muted/10">
                  <th className="px-3 py-2 text-left font-semibold">Client</th>
                  <th className="px-3 py-2 text-right font-semibold">Revenue</th>
                  <th className="px-3 py-2 text-right font-semibold">Profit</th>
                  <th className="px-3 py-2 text-right font-semibold">Inv.</th>
                </tr></thead>
                <tbody>
                  {data.by_client.map((c, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/10">
                      <td className="px-3 py-2 font-medium">{c.client}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(c.revenue)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-600">{fmt(c.profit)}</td>
                      <td className="px-3 py-2 text-right">{c.invoices}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.by_supplier?.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden" data-testid="pl-by-supplier">
            <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center gap-2">
              <Truck size={16} className="text-purple-500" />
              <h3 className="text-sm font-semibold font-[Chivo]">Costs by Supplier</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-border bg-muted/10">
                  <th className="px-3 py-2 text-left font-semibold">Supplier</th>
                  <th className="px-3 py-2 text-right font-semibold">Total Cost</th>
                  <th className="px-3 py-2 text-right font-semibold">Jobs</th>
                </tr></thead>
                <tbody>
                  {data.by_supplier.map((s, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-muted/10">
                      <td className="px-3 py-2 font-medium">{s.supplier}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(s.total_cost)}</td>
                      <td className="px-3 py-2 text-right">{s.jobs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
